import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Check, ExternalLink, Code2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable embed-snippet card. Renders a public direct-link + an <iframe>
 * snippet ready to paste. Used by:
 *   - Settings (sitewide embed of an employer's careers page)
 *   - JobDetail (per-role embed of a single open role)
 */
export default function EmbedSnippet({
  title,
  description,
  publicUrl,
  iframeTitle = "Open roles",
  height = 700,
  testIdPrefix = "embed",
}) {
  const [copiedKey, setCopiedKey] = useState(null);

  const iframe = `<iframe
  src="${publicUrl}"
  width="100%"
  height="${height}"
  style="border:0;max-width:880px;display:block;margin:0 auto;"
  loading="lazy"
  title="${iframeTitle.replace(/"/g, "&quot;")}"
></iframe>`;

  const copy = async (text, key, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(`${label} copied`);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <section
      data-testid={`${testIdPrefix}-card`}
      className="border border-slate-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
          <Code2 className="h-3 w-3" /> {title}
        </div>
        <a
          href={publicUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testIdPrefix}-preview`}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Preview
        </a>
      </div>
      <div className="space-y-4 p-5">
        {description && (
          <p className="text-sm text-black">{description}</p>
        )}

        {/* Direct link */}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Direct link
          </div>
          <div className="mt-2 flex items-stretch gap-2">
            <input
              data-testid={`${testIdPrefix}-link-input`}
              readOnly
              value={publicUrl}
              onClick={(e) => e.target.select()}
              className="flex-1 border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Button
              size="sm"
              variant="outline"
              data-testid={`${testIdPrefix}-link-copy`}
              onClick={() => copy(publicUrl, "link", "Link")}
              className="rounded-sm border-slate-300"
            >
              {copiedKey === "link" ? (
                <Check className="h-3.5 w-3.5 text-emerald-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>

        {/* iframe snippet */}
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
            HTML embed (iframe)
          </div>
          <textarea
            data-testid={`${testIdPrefix}-iframe-textarea`}
            readOnly
            value={iframe}
            onClick={(e) => e.target.select()}
            rows={5}
            className="mt-2 w-full resize-none border border-slate-300 bg-slate-50 p-3 font-mono text-[11px] leading-relaxed text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="mt-2 flex justify-end">
            <Button
              size="sm"
              data-testid={`${testIdPrefix}-iframe-copy`}
              onClick={() => copy(iframe, "iframe", "Embed snippet")}
              className="rounded-sm bg-blue-700 text-white hover:bg-blue-600"
            >
              {copiedKey === "iframe" ? (
                <>
                  <Check className="mr-1.5 h-3.5 w-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy embed
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
